import React, { useState } from 'react';
import { Table, TableColumnsType, Button, Space, Modal } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import FilterArea, { FilterOptions, DEFAULT_FILTER_OPTIONS } from './components/FilterArea';
import {
  GET_ADDRESS_RESIDENTIAL_AREA_LISTING,
  GetAddressResidentialAreaListingResponse,
  AddressResidentialAreaType,
} from './graphql/queries/getAddressResidentialAreaListing';
import {
  DELETE_ADDRESS_RESIDENTIAL_AREA,
} from './graphql/mutations/upsertAddressResidentialArea';
import AddressResidentialAreaDrawer from './components/AddressResidentialAreaDrawer';
import AddAddressResidentialAreaDrawer from './components/AddAddressResidentialAreaDrawer';
import { useTranslation } from 'react-i18next';
import { HasResource } from '../../../components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';

const AddressResidentialAreaPage: React.FC = () => {
  const [filterOptions, setFilterOptions] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
  const [appliedFilters, setAppliedFilters] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null);
  const [drawerMode, setDrawerMode] = useState<'view' | 'edit'>('edit');
  const [addDrawerVisible, setAddDrawerVisible] = useState(false);
  /** 每次打开新增抽屉时递增，用于 key 强制重挂载以清空表单与图片缓存 */
  const [addDrawerKey, setAddDrawerKey] = useState(0);
  const { t } = useTranslation();

  const { data, loading, refetch } = useQuery<GetAddressResidentialAreaListingResponse>(
    GET_ADDRESS_RESIDENTIAL_AREA_LISTING,
    {
      variables: {
        residentialAddress: appliedFilters.residentialAddress || undefined,
        residentialName: appliedFilters.residentialName || undefined,
        cityName: appliedFilters.cityName || undefined,
        stateAbbreviation: appliedFilters.stateAbbreviation || undefined,
        zipcode: appliedFilters.zipcode || undefined,
        dataType: 'OFFICIAL',
        page: currentPage,
        size: pageSize,
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  const [deleteArea] = useMutation(DELETE_ADDRESS_RESIDENTIAL_AREA);

  const handleFilter = () => {
    setAppliedFilters({ ...filterOptions });
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    const defaults = DEFAULT_FILTER_OPTIONS;
    setFilterOptions(defaults);
    setAppliedFilters(defaults);
    setCurrentPage(1);
  };

  const handleTableChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const openDetailDrawer = (record: AddressResidentialAreaType, mode: 'view' | 'edit') => {
    setSelectedAreaId(record.id);
    setDrawerMode(mode);
    setDrawerVisible(true);
  };

  const handleDelete = async (record: AddressResidentialAreaType) => {
    await deleteArea({
      variables: {
        id: Number(record.id),
      },
    });
    refetch();
  };

  const showDeleteConfirm = (record: AddressResidentialAreaType) => {
    Modal.confirm({
      title: t('mapTool.addressResidentialArea.deleteTitle') as string,
      content: t('mapTool.addressResidentialArea.deleteContent', {
        residentialName: record.residentialName || record.id,
      }) as string,
      okText: t('components.button.confirm') as string,
      okType: 'danger',
      cancelText: t('components.button.cancel') as string,
      onOk: () => handleDelete(record),
    });
  };

  const columns: TableColumnsType<AddressResidentialAreaType> = [
    {
      title: t('mapTool.addressResidentialArea.table.id'),
      dataIndex: 'id',
      key: 'id',
      width: 100,
      align: 'center',
      render: (id: string, record: AddressResidentialAreaType) => (
        <Button
          type="link"
          size="small"
          className="p-0 h-auto font-normal"
          onClick={() => openDetailDrawer(record, 'view')}
        >
          {id}
        </Button>
      ),
    },
    {
      title: t('mapTool.addressResidentialArea.table.residentialName'),
      dataIndex: 'residentialName',
      key: 'residentialName',
      width: 240,
      ellipsis: true,
    },
    {
      title: t('mapTool.addressResidentialArea.table.residentialAddress'),
      dataIndex: 'residentialAddress',
      key: 'residentialAddress',
      width: 360,
      ellipsis: true,
    },
    {
      title: t('mapTool.addressResidentialArea.table.standardAddress'),
      dataIndex: 'standardAddress',
      key: 'standardAddress',
      width: 360,
      ellipsis: true,
    },
    {
      title: t('mapTool.addressResidentialArea.table.stateAbbreviation'),
      dataIndex: 'stateAbbreviation',
      key: 'stateAbbreviation',
      width: 80,
      align: 'center',
    },
    {
      title: t('mapTool.addressResidentialArea.table.cityName'),
      dataIndex: 'cityName',
      key: 'cityName',
      width: 160,
      ellipsis: true,
    },
    {
      title: t('mapTool.addressResidentialArea.table.zipcode'),
      dataIndex: 'zipcode',
      key: 'zipcode',
      width: 120,
      align: 'center',
    },
    {
      title: t('mapTool.addressResidentialArea.table.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      align: 'center',
      render: (v: string | null | undefined) =>
        v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '—',
    },
    {
      title: t('mapTool.addressResidentialArea.table.updatedAt'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 180,
      align: 'center',
      render: (v: string | null | undefined) =>
        v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '—',
    },
    {
      title: t('components.button.action') as string,
      key: 'action',
      width: 140,
      fixed: 'right' as const,
      align: 'center',
      render: (_, record) => (
        <Space size="middle">
          <HasResource resourceCodes={[ResourceCode.addressResidentialArea.UPDATE]}>
            <Button
              type="link"
              size="small"
              onClick={() => openDetailDrawer(record, 'edit')}
            >
              {t('components.button.edit')}
            </Button>
          </HasResource>
          <HasResource resourceCodes={[ResourceCode.addressResidentialArea.DELETE]}>
            <Button
              type="link"
              size="small"
              danger
              onClick={() => showDeleteConfirm(record)}
            >
              {t('components.button.delete')}
            </Button>
          </HasResource>
        </Space>
      ),
    },
  ];

  const pageData = data?.addressResidentialAreaList;

  return (
    <div className="p-6">
      <h4 className="text-xl font-bold mb-4">{t('mapTool.addressResidentialArea.pageTitle')}</h4>

      <FilterArea filters={filterOptions} setFilters={setFilterOptions} />

      {/* 按钮区域，遵循 cooperation-warehouse 命名规范：新建 / 筛选 / 重置 */}
      <div className="flex justify-end items-center mb-4 mt-4">
        <div className="flex items-center gap-2">
          <HasResource resourceCodes={[ResourceCode.addressResidentialArea.CREATE]}>
            <Button
              type="primary"
              onClick={() => {
                setAddDrawerKey((k) => k + 1);
                setAddDrawerVisible(true);
              }}
              className="w-auto min-w-24"
            >
              <span className="font-bold">{t('components.button.new')}</span>
            </Button>
          </HasResource>
          <Button
            type="primary"
            onClick={handleFilter}
            className="w-auto min-w-24"
          >
            <span className="font-bold">{t('components.button.filter')}</span>
          </Button>
          <Button
            onClick={handleResetFilters}
            className="w-auto min-w-24"
          >
            <span className="font-bold">{t('components.button.reset')}</span>
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <Table
          columns={columns}
          dataSource={pageData?.content || []}
          loading={loading}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: pageData?.totalElements || 0,
            showSizeChanger: true,
            showTotal: (total) => t('mapTool.addressResidentialArea.table.paginationTotal', { total }),
            onChange: handleTableChange,
            onShowSizeChange: handleTableChange,
          }}
        />
      </div>

      <AddressResidentialAreaDrawer
        visible={drawerVisible}
        areaId={selectedAreaId}
        mode={drawerMode}
        onClose={() => {
          setDrawerVisible(false);
          setSelectedAreaId(null);
          refetch();
        }}
        onSwitchToEdit={() => setDrawerMode('edit')}
      />

      <AddAddressResidentialAreaDrawer
        key={addDrawerKey}
        visible={addDrawerVisible}
        onClose={() => setAddDrawerVisible(false)}
        onSuccess={() => refetch()}
        onSwitchToEdit={(areaId) => {
          setAddDrawerVisible(false);
          setSelectedAreaId(areaId);
          setDrawerMode('edit');
          setDrawerVisible(true);
        }}
      />
    </div>
  );
};

export default AddressResidentialAreaPage;

